import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { http } from '../../../lib/api';
import { AppLayout } from '../../../components/layout';
import { Button, Card, Input, Select, Badge, EmptyState, SpinnerBlock, Modal, Textarea } from '../../../components/core';
import { DataTable, type Column, Pagination } from '../../../components/feedback';
import { ADMIN_NAV } from '../nav';
import { subscribeRealtimeEvents } from '../../../lib/ws';
import type { ConversationDto, ConversationStatus, ChatPaginationParams, CreateConversationDto } from '@livestreamx/shared-types';

interface ConversationsResponse {
  items: ConversationDto[];
  total: number;
  page: number;
  pageSize: number;
}

const STATUS_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'OPEN', label: 'Open' },
  { value: 'CLOSED', label: 'Closed' },
  { value: 'ARCHIVED', label: 'Archived' },
] as const;

const STATUS_COLORS: Record<ConversationStatus, string> = {
  OPEN: 'active',
  CLOSED: 'warning',
  ARCHIVED: 'archived',
};

export default function AdminChatList() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'ALL'>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const [newConvData, setNewConvData] = useState<CreateConversationDto>({ tenantId: '', subject: '', initialMessage: '' });
  const [tenants, setTenants] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);

  const loadTenants = async () => {
    setTenantsLoading(true);
    try {
      const res = await http.get<{ items: { id: string; name: string; slug: string; status?: string }[] }>('/api/admin/tenants?pageSize=500');
      setTenants(res.items.filter((t) => t.status === 'ACTIVE'));
    } catch {
      setTenants([]);
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadConversations = async () => {
    setLoading(true);
    try {
      const params: ChatPaginationParams = { page, pageSize, status: statusFilter };
      const query = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => v !== undefined && query.set(k, String(v)));
      const res = await http.get<ConversationsResponse>(`/api/chat/conversations?${query}`);
      setConversations(res.items);
      setTotal(res.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConversations();
    const unsubscribe = subscribeRealtimeEvents((event: unknown) => {
      const e = event as { type: string; data: ConversationDto };
      if (e.type === 'chat.conversation.created' || e.type === 'chat.conversation.updated') {
        setConversations((prev) => {
          const idx = prev.findIndex((c) => c.id === e.data.id);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = e.data;
            return next.sort((a, b) => new Date(b.lastMessageAt ?? '').getTime() - new Date(a.lastMessageAt ?? '').getTime());
          }
          return [e.data, ...prev].sort((a, b) => new Date(b.lastMessageAt ?? '').getTime() - new Date(a.lastMessageAt ?? '').getTime());
        });
      }
    });
    return unsubscribe;
  }, [page, statusFilter]);

  const handleCreate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setCreating(true);
    setCreateError('');
    try {
      await http.post('/api/chat/conversations', newConvData);
      setShowCreateModal(false);
      setNewConvData({ tenantId: '', subject: '', initialMessage: '' });
      loadConversations();
    } catch (err: unknown) {
      setCreateError((err as Error).message ?? 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  };

  const handleStatusChange = async (id: string, status: ConversationStatus) => {
    try {
      await http.patch(`/api/chat/conversations/${id}/status`, { status });
      loadConversations();
    } catch (e) {
      console.error(e);
    }
  };

  const columns: Column<ConversationDto>[] = [
    { key: 'tenantName', header: 'Tenant', render: (c) => (
      <Link to={`/admin/chat/${c.id}`} className="font-medium hover:underline">{c.tenantName}</Link>
    )},
    { key: 'tenantSlug', header: 'Slug', render: (c) => <span className="text-muted text-sm">@{c.tenantSlug}</span> },
    { key: 'subject', header: 'Subject', render: (c) => c.subject ?? <span className="text-muted">—</span> },
    { key: 'status', header: 'Status', render: (c) => <Badge tone={STATUS_COLORS[c.status]}>{c.status}</Badge> },
    { key: 'lastMessagePreview', header: 'Last Message', render: (c) => (
      <span className="text-sm max-w-xs truncate block">{c.lastMessagePreview ?? <span className="text-muted">—</span>}</span>
    )},
    { key: 'lastMessageAt', header: 'Last Activity', render: (c) => c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleString() : <span className="text-muted">—</span> },
    { key: 'unreadAdmin', header: 'Unread', render: (c) => c.unreadAdmin > 0 ? <Badge tone="error">{c.unreadAdmin}</Badge> : <span className="text-muted">0</span> },
    {
      key: 'actions',
      header: 'Actions',
      render: (c) => (
        <div className="flex gap-1">
          <Link to={`/admin/chat/${c.id}`}>
            <Button variant="ghost" size="sm">Open</Button>
          </Link>
          {c.status === 'OPEN' && (
            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(c.id, 'CLOSED')}>
              Close
            </Button>
          )}
          {c.status === 'CLOSED' && (
            <Button variant="ghost" size="sm" onClick={() => handleStatusChange(c.id, 'OPEN')}>
              Reopen
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(c.id, 'ARCHIVED')}>
            Archive
          </Button>
        </div>
      ),
    },
  ];

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AppLayout brand="LiveStreamX" navGroups={ADMIN_NAV} variant="admin">
      <div className="page-header">
        <div>
          <h1 className="page-title">Chat Conversations</h1>
          <p className="page-subtitle">Manage tenant support conversations</p>
        </div>
        <Button onClick={() => { loadTenants(); setShowCreateModal(true); }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
          New Conversation
        </Button>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-2">
          <Select
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as ConversationStatus | 'ALL')}
            options={STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
        </div>
        {loading ? (
          <SpinnerBlock label="Loading conversations…" />
        ) : conversations.length === 0 ? (
          <EmptyState title="No conversations" body="Start a new conversation with a tenant." />
        ) : (
          <DataTable columns={columns} rows={conversations} />
        )}
        <Pagination page={page} totalPages={totalPages} onPage={setPage} />
      </Card>

      {showCreateModal && (
        <Modal
          title="New Conversation"
          onClose={() => setShowCreateModal(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button disabled={creating || !newConvData.tenantId || !newConvData.initialMessage.trim()} onClick={() => void handleCreate()}>
                {creating ? 'Creating…' : 'Create'}
              </Button>
            </>
          }
        >
          <form onSubmit={(e) => void handleCreate(e)}>
            <div className="mb-4">
              <Select
                label="Tenant *"
                value={newConvData.tenantId}
                onChange={(v) => setNewConvData({ ...newConvData, tenantId: v })}
                options={[{ value: '', label: tenantsLoading ? 'Loading tenants…' : 'Select a tenant' }, ...tenants.map((t) => ({ value: t.id, label: `${t.name} (@${t.slug})` }))]}
                disabled={tenantsLoading}
              />
            </div>
            <div className="mb-4">
              <Input
                label="Subject"
                value={newConvData.subject ?? ''}
                onChange={(v) => setNewConvData({ ...newConvData, subject: v })}
                placeholder="Optional subject line"
              />
            </div>
            <div className="mb-4">
              <Textarea
                label="Initial Message *"
                value={newConvData.initialMessage ?? ''}
                onChange={(v) => setNewConvData({ ...newConvData, initialMessage: v })}
                placeholder="First message to send…"
                rows={4}
              />
            </div>
            {createError && <div className="text-sm mb-2" style={{ color: 'var(--danger)' }}>{createError}</div>}
          </form>
        </Modal>
      )}
    </AppLayout>
  );
}